import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/lib/api'
import { reconciliationApi } from '@/lib/api'
import type { DuplicateFileInfo } from '@/components/DuplicateFileDialog'
import type { FileStatus } from './useReconApi'

export type UploadTrackerStatus =
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'duplicate'

export interface UploadedFileTracker {
  id: string
  fileId: string
  filename: string
  status: UploadTrackerStatus
  message?: string
  transactionCount?: number
  file?: File
  duplicate?: DuplicateFileInfo
}

interface UploadResponse {
  message: string
  file_id?: string
  duplicate?: boolean
  existing_file?: DuplicateFileInfo
  checksum?: string
}

interface UseUploadTrackerOptions {
  clientId: string | null | undefined
  bankAccountId: number | null | undefined
  onUploadComplete: (fileId: string) => void
}

let trackerSeq = 0
const nextId = () => `upl-${++trackerSeq}-${Date.now()}`

export function useUploadTracker({ clientId, bankAccountId, onUploadComplete }: UseUploadTrackerOptions) {
  const [files, setFiles] = useState<UploadedFileTracker[]>([])
  const [error, setError] = useState<string | null>(null)
  const pollIntervalRef = useRef<number>()

  const isUploading = files.some((f) => f.status === 'uploading')
  const hasProcessing = files.some((f) => f.status === 'processing')
  const hasDuplicate = files.some((f) => f.status === 'duplicate')
  const allDone =
    files.length > 0 &&
    files.every(
      (f) => f.status === 'completed' || f.status === 'failed'
    )

  useEffect(() => {
    if (!hasProcessing) {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      return
    }

    pollIntervalRef.current = window.setInterval(async () => {
      const processing = files.filter((f) => f.status === 'processing')
      for (const file of processing) {
        try {
          const res = await apiClient.get<FileStatus>(
            `/reconciliation/upload/${file.fileId}/status`
          )
          if (res.status !== 'processing' && res.status !== 'pending') {
            setFiles((prev) =>
              prev.map((f) =>
                f.fileId === file.fileId
                  ? {
                      ...f,
                      status: res.status as 'completed' | 'failed',
                      message: res.message,
                      transactionCount: res.transactions_count,
                    }
                  : f
              )
            )
            if (res.status === 'completed') {
              onUploadComplete(file.fileId)
            }
          }
        } catch {
          // Polling failure — keep trying
        }
      }
    }, 3000)

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    }
  }, [hasProcessing, files, onUploadComplete])

  const performUpload = useCallback(
    async (trackerId: string, file: File) => {
      if (!clientId || bankAccountId == null) return
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('client_id', clientId)
        formData.append('bank_account_id', String(bankAccountId))

        const res = await apiClient.post<UploadResponse>('/reconciliation/upload', formData)

        if (res.duplicate && res.existing_file) {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === trackerId
                ? {
                    ...f,
                    status: 'duplicate',
                    duplicate: res.existing_file,
                    message: 'Already uploaded',
                    file,
                  }
                : f
            )
          )
          return
        }

        if (!res.file_id) {
          throw new Error('Upload succeeded but no file id was returned')
        }

        setFiles((prev) =>
          prev.map((f) =>
            f.id === trackerId
              ? { ...f, fileId: res.file_id!, status: 'processing', file: undefined }
              : f
          )
        )
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Upload failed'
        setFiles((prev) =>
          prev.map((f) =>
            f.id === trackerId ? { ...f, status: 'failed', message: msg } : f
          )
        )
        setError(msg)
      }
    },
    [clientId, bankAccountId]
  )

  const uploadFile = useCallback(
    async (file: File) => {
      if (!clientId || bankAccountId == null) return
      setError(null)
      const id = nextId()
      setFiles((prev) => [
        ...prev,
        { id, fileId: '', filename: file.name, status: 'uploading', file },
      ])
      await performUpload(id, file)
    },
    [clientId, bankAccountId, performUpload]
  )

  const uploadFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList?.length) return
      for (let i = 0; i < fileList.length; i++) {
        uploadFile(fileList[i])
      }
    },
    [uploadFile]
  )

  const reprocessDuplicate = useCallback(
    async (trackerId: string, force: boolean) => {
      const tracker = files.find((f) => f.id === trackerId)
      if (!tracker || !tracker.duplicate || !tracker.file) return
      const existingFileId = tracker.duplicate.id
      const file = tracker.file

      setFiles((prev) =>
        prev.map((f) =>
          f.id === trackerId
            ? { ...f, status: 'uploading', message: 'Reprocessing…', duplicate: undefined }
            : f
        )
      )

      try {
        await reconciliationApi.reprocessDuplicateFile(existingFileId, force)
        await performUpload(trackerId, file)
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          (err as Error)?.message ||
          'Reprocess failed'
        setFiles((prev) =>
          prev.map((f) =>
            f.id === trackerId ? { ...f, status: 'failed', message: msg } : f
          )
        )
      }
    },
    [files, performUpload]
  )

  const cancelDuplicate = useCallback((trackerId: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === trackerId
          ? { ...f, status: 'failed', message: 'Skipped (duplicate)', duplicate: undefined, file: undefined }
          : f
      )
    )
  }, [])

  const dismiss = useCallback(() => {
    setFiles([])
    setError(null)
  }, [])

  return {
    files,
    error,
    isUploading,
    hasProcessing,
    hasDuplicate,
    allDone,
    uploadFiles,
    reprocessDuplicate,
    cancelDuplicate,
    dismiss,
  }
}
